import { Company } from "@domain/CRM/company";
import { Grower } from "@domain/CRM/grower";
import { Job, type MapFeatureEdit } from "@domain/CRM/job";
import { StandardAxiosConfig, getBaseDataURL, parseDates } from "../../../common";
import { CreateAPI_Base, DataAPI_Base, GetAPI_Base, UpdateAPI_Base } from "../base";
import { postJobDropoffs, postJobLocations, postJobMapFeatureEdits, postJobOrders } from "./jobs.schedulingWrites";
import { JobIssuesAPI } from "./jobs.issues";
import { apiClient } from "../../../client";
import { inflateText, inflatedLines } from "../../../inflate";
import { IAirtableAttachment } from "@domain/CRM/Attachment";

/** Wire records as `Job` instances, their date fields parsed. */
function hydrateJobs(records: readonly Job[]): Job[] {
	const jobs = records.map((j) => Object.assign(new Job(), j));
	parseDates<Job>(jobs);
	return jobs;
}

/** A zlib-compressed JSON job list (an `isCompressed` response's Blob body), hydrated. A body
 *  that does not inflate whole rejects. */
async function readCompressedJobs(response: { data: unknown }): Promise<Job[]> {
	const text = await inflateText(await (response.data as Blob).arrayBuffer());
	return hydrateJobs(JSON.parse(text) as Job[]);
}

/**
 * Jobs create API — transport-only. The `many`/`one` methods are inherited
 * from `CreateAPI_Base` and make the raw HTTP POST. Callers that need the
 * full creation pipeline (associate growers, salespersons, branches, prep
 * payload) should use `submitJobs` / `submitJob` from
 * `domain/job/jobCreationService` instead of calling this class directly.
 */
class CreateAPI_Jobs extends CreateAPI_Base<Job> {
	constructor() {
		super("jobs");
	}
}

class GetAPI_Jobs extends GetAPI_Base<Job> {
	constructor() {
		super("jobs", Job, true);
	}

	public async byCompany(company: Company, branchNames?: string[]): Promise<Job[]> {
		if (!company.name) return [];
		// Compressed (zlib) and lite: several thousand jobs are ~52MB raw JSON,
		// most of it attachment URL arrays. The lite payload omits attachments
		// (job.attachments === null); consumers load them on demand via
		// jobsList.ensureAttachments().
		const url = `${getBaseDataURL("jobs")}/get-by-company?compressed=true&include_attachments=false`;
		const body: { company_name: string; branch_names?: string[] } = { company_name: company.name };
		if (branchNames && branchNames.length > 0) body.branch_names = branchNames;
		const response = await apiClient.post(url, body, StandardAxiosConfig(0, { isCompressed: true }));
		if (response.status !== 200) {
			throw new Error(`Failed to get jobs for company`);
		}
		return readCompressedJobs(response);
	}

	/**
	 * Retrieves `company`'s unready jobs linked to one of `growers`, matched by grower id (the names only narrow the query).
	 *
	 * @param growers - An array of `Grower` objects to filter unready jobs by. If empty or undefined, returns an empty array.
	 */
	public async byGrowers(company: Company, growers: Grower[]): Promise<Job[]> {
		if (!growers || growers.length === 0) return [];
		const url = `${getBaseDataURL("jobs")}/get-unready-for-grower`;
		const ids = growers.map((g) => g.id);
		const body = { company_name: company.name, grower_ids: ids, grower_names: growers.map((g) => g.name) };
		const response = await apiClient.post<Job[]>(url, body, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to get unready jobs for growers`);
		}
		return hydrateJobs(response.data);
	}

	public async unscheduled(): Promise<Job[]> {
		const url = `${getBaseDataURL("jobs")}/get-unscheduled`;
		const response = await apiClient.post<Job[]>(url, {}, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to get unscheduled jobs`);
		}
		return hydrateJobs(response.data);
	}

	/**
	 * Streaming variant of `byCompany`: the server sends zlib-compressed NDJSON,
	 * one line per batch of (attachment-less) jobs, flushed as Airtable pages
	 * arrive. `onBatch` fires per line so callers can render progressively;
	 * resolves with the full list once the stream ends.
	 */
	public async byCompanyStream(
		company: Company,
		branchNames: string[] | undefined,
		onBatch: (jobs: Job[]) => void,
	): Promise<Job[]> {
		if (!company.name) return [];
		const body: { company_name: string; branch_names?: string[] } = { company_name: company.name };
		if (branchNames && branchNames.length > 0) body.branch_names = branchNames;
		return this.streamJobs(`${getBaseDataURL("jobs")}/get-by-company/stream`, body, onBatch);
	}

	/**
	 * Streaming variant of `unscheduled` for the scheduling map: lite jobs plus the inline
	 * boundary and map-feature texts, batched as the server fetches pages. `attachments` is null
	 * when the API judged the inline boundary text usable (it parses, with at least one feature).
	 */
	public async unscheduledStream(onBatch: (jobs: Job[]) => void): Promise<Job[]> {
		return this.streamJobs(`${getBaseDataURL("jobs")}/get-unscheduled/stream`, {}, onBatch);
	}

	/**
	 * The scheduling board's scheduled jobs: every job on a shift that is NOT sampled (no "Sample
	 * Date"), filtered Airtable-side on the job's own fields — no shift list needed, and every
	 * shift's jobs, so the caller narrows to the shifts it shows. `unscheduledStream`'s payload,
	 * with the boundary shapefile beside the GeoJSON attachment on the jobs that carry them.
	 *
	 * One compressed list through `apiClient`, exactly like the by-ids `POST /jobs/get` it
	 * replaced — never a stream: a partly loaded set would read as "the rest are sampled", and a
	 * failure has to reach the interceptor as it did (toast, Sentry, session-expiry logout).
	 */
	public async scheduledUnsampled(): Promise<Job[]> {
		const url = `${getBaseDataURL("jobs")}/get-scheduled-unsampled`;
		const response = await apiClient.post(url, {}, StandardAxiosConfig(0, { isCompressed: true }));
		// A body that does not inflate whole rejects: the read fails rather than resolve with the
		// jobs that did arrive.
		return readCompressedJobs(response);
	}

	/** Every job modified after `since`, in any state, with `scheduledUnsampled`'s payload: the
	 *  scheduling board's background pick-up of new jobs. A failure does not toast (the
	 *  interceptor still reports it, and still logs out an expired session). */
	public async modifiedSince(since: Date): Promise<Job[]> {
		const url = `${getBaseDataURL("jobs")}/get-modified-since`;
		const config = { ...StandardAxiosConfig(0, { isCompressed: true }), suppressErrorToast: true };
		const response = await apiClient.post(url, { since: since.toISOString() }, config);
		return readCompressedJobs(response);
	}

	/**
	 * POST to a jobs streaming endpoint (zlib-compressed NDJSON; one line per
	 * batch) and surface each batch via `onBatch`; resolves with the full list.
	 *
	 * Uses `fetch` (axios can't stream response bodies in the browser), so the
	 * apiClient interceptors don't apply — auth headers come from
	 * StandardAxiosConfig directly.
	 */
	private async streamJobs(url: string, body: unknown, onBatch: (jobs: Job[]) => void): Promise<Job[]> {
		const config = StandardAxiosConfig();
		const response = await fetch(url, {
			method: "POST",
			headers: { ...(config.headers as Record<string, string>), "Content-Type": "application/json" },
			body: JSON.stringify(body),
		});
		if (!response.ok || !response.body) {
			throw new Error(`Failed to stream jobs`);
		}

		const all: Job[] = [];
		const lines = inflatedLines(response.body);
		while (true) {
			let next: IteratorResult<string>;
			try {
				next = await lines.next();
			} catch (error) {
				const message = error instanceof Error ? error.message : String(error);
				throw new Error(`Failed to decompress jobs stream: ${message}`);
			}
			if (next.done) break;
			const jobs = hydrateJobs(JSON.parse(next.value) as Job[]);
			all.push(...jobs);
			onBatch(jobs);
		}
		return all;
	}

	public async attachmentsByIds(ids: string[]): Promise<Record<string, Job["attachments"]>> {
		if (ids.length === 0) return {};
		const url = `${getBaseDataURL("jobs")}/get-attachments`;
		const response = await apiClient.post<Record<string, Job["attachments"]>>(
			url,
			{ job_ids: ids },
			StandardAxiosConfig(),
		);
		if (response.status !== 200) {
			throw new Error(`Failed to get job attachments`);
		}
		return response.data;
	}

	// `fresh` bypasses the day-long metadata cache behind this endpoint (a separate
	// `Cache` the generic data-router's `force_refresh` can't reach; M9: `?fresh=1`).
	public async lastCropOptions(fresh?: boolean): Promise<string[]> {
		const url = `${getBaseDataURL("jobs")}/options/last-crop${fresh ? "?fresh=1" : ""}`;
		const response = await apiClient.get<string[]>(url, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to get last crop options`);
		} else {
			return response.data;
		}
	}

	public async nextCropOptions(fresh?: boolean): Promise<string[]> {
		const url = `${getBaseDataURL("jobs")}/options/next-crop${fresh ? "?fresh=1" : ""}`;
		const response = await apiClient.get<string[]>(url, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to get next crop options`);
		} else {
			return response.data;
		}
	}
}

class UpdateAPI_Jobs extends UpdateAPI_Base<Job> {
	constructor() {
		super("jobs");
	}

	/**
	 * Update a single field on a single Job. `field` is a `keyof Job` (e.g.
	 * `"dealIds"`, `"branchIds"`); the BE translates this to the underlying
	 * Airtable column. `value` must be in the shape Airtable expects: `["recXXX"]`
	 * for a linked-record field, a string for text, etc.
	 *
	 * Use this instead of `many([fullJob])` when you've loaded a Job via a lite
	 * payload (matcher pages, etc.) — `many` round-trips every field, which would
	 * clear any column not present in the lite payload.
	 *
	 * The route writes only its allowlist (`_WRITABLE_FIELDS` in `routers/data/jobs.py`:
	 * `dealIds`, `branchIds`, and `mgrNotes` / `csvSentToLab` for ROGO users) — anything else is a 400, so a
	 * new PATCH here needs the field added there first.
	 */
	async field<K extends keyof Job>(id: string, field: K, value: unknown): Promise<Job> {
		const url = `${getBaseDataURL("jobs")}/update/${id}/${field}`;
		// Serialized here rather than left to axios. Axios only infers `application/json` for
		// objects and arrays; hand it a bare string or number and it sends
		// `application/x-www-form-urlencoded` with the raw value as the body. FastAPI's `Body()`
		// then parses nothing out of it and the route writes an empty value — a 200 that saved
		// NOTHING. Every caller until now passed a linked-record array, which is why the bug sat
		// here unseen; the Skips queue's notes column is the first plain-string write.
		const config = StandardAxiosConfig();
		(config.headers as Record<string, string>)["Content-Type"] = "application/json";
		const response = await apiClient.post<Job>(url, JSON.stringify(value), config);
		if (response.status !== 200) {
			throw new Error(`Failed to update '${String(field)}' on job ${id}: ${response.statusText}`);
		}
		const updated = Object.assign(new Job(), response.data);
		parseDates<Job>([updated]);
		return updated;
	}

	/**
	 * Recalculate the job's status (central-nervous-system) and resolve to the refreshed job.
	 *
	 * The calculator runs itself off a queue and a five-minute schedule, so this only asks it
	 * sooner. The API waits for it and reads the job back, so the resolved Job already carries the
	 * new status — do NOT refetch instead, that races the calculator's write.
	 */
	async recalculateStatus(id: string): Promise<Job> {
		const url = `${getBaseDataURL("jobs")}/recalculate-status/${encodeURIComponent(id)}`;
		// The standard config's default is no client timeout, which is what this wants: the API
		// bounds the wait at 300s and answers 502 past that, so the deadline stays on one side.
		const response = await apiClient.post<Job>(url, undefined, StandardAxiosConfig());
		if (response.status !== 200) throw new Error(`Failed to recalculate the status of job ${id}`);
		const updated = Object.assign(new Job(), response.data);
		parseDates<Job>([updated]);
		return updated;
	}

	/** Scheduling's four batch partial-writes. Bodies live in `jobs.schedulingWrites.ts`,
	 *  mirroring the backend sub-router that serves them. */
	async orders(updates: { jobId: string; order: number | null }[]): Promise<void> {
		return postJobOrders(updates);
	}

	async dropoffs(updates: { jobId: string; shiftDropoffId: string | null }[]): Promise<void> {
		return postJobDropoffs(updates);
	}

	async locations(updates: { jobId: string; location: string | null }[]): Promise<void> {
		return postJobLocations(updates);
	}

	async mapFeatures(edits: MapFeatureEdit[]): Promise<void> {
		return postJobMapFeatureEdits(edits);
	}

	async files(id: string, fileList: File[]) {
		const url = `${getBaseDataURL("jobs")}/upload-file/${id}`;

		async function upload(file: File) {
			const formData = new FormData();
			formData.append("file", file);

			const response = await apiClient.post(url, formData, StandardAxiosConfig(10, { isFileUpload: true }));
			if (response.status !== 200) {
				throw new Error(`Failed to upload file: ${file.name}`);
			} else {
				return response.data;
			}
		}

		if (!fileList) return [];
		const fileArray = Array.from(fileList);
		const uploadResults = await Promise.all(
			fileArray.map(async (file) => {
				const s3url = await upload(file);
				return { url: s3url, filename: file.name } as IAirtableAttachment;
			}),
		);

		return uploadResults;
	}
}

export class JobsAPI extends DataAPI_Base<Job> {
	get: GetAPI_Jobs;
	create: CreateAPI_Jobs;
	update: UpdateAPI_Jobs;
	issues: JobIssuesAPI;

	// job data is compressed, as it can get quite large
	constructor() {
		super("jobs", Job, true);
		this.get = new GetAPI_Jobs();
		this.create = new CreateAPI_Jobs();
		this.update = new UpdateAPI_Jobs();
		this.issues = new JobIssuesAPI();
	}
}
